import { useIntl } from 'react-intl';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { IdentityProvidersMessages } from '../../IdentityProviders.messages';
import { externalIdpLogoutConfigSchema } from 'pages/IdentityProviders/schema/identityProviderSchema';

type UseCreateSamlProviderForm = () => UseFormReturn;

export const useCreateSamlProviderForm: UseCreateSamlProviderForm = () => {
  const intl = useIntl();
  const schema = yup.object().shape(
    {
      name: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.nameRequiredError))
        .min(3, 'Identity provider name must be at least 3 characters long')
        .max(256, 'Identity provider name can not be longer than 256 characters'),
      userType: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.userTypeRequiredError))
        .oneOf(['Staff', 'Shoppers']),
      externalIdField: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.externalIdRequiredError))
        .oneOf(['Uid', 'Email', 'PhoneNumber']),
      entityId: yup
        .string()
        .required('Entity Id is required')
        .max(512, 'Entity Id can not be longer than 512 characters'),
      ssoUrl: yup.string().required('SSO URL is required').url('Invalid URL provided'),
      rpEntityId: yup.string().required(''),
      externalIdpLogoutConfig: externalIdpLogoutConfigSchema,
      certificates: yup
        .array()
        .required('At least one certificate is required')
        .min(1, 'At least one certificate required')
        .of(
          yup.object().shape({
            value: yup
              .string()
              .test(
                'valid-cert',
                'Must start with "-----BEGIN CERTIFICATE-----", and end with "-----END CERTIFICATE-----".',
                (val) => {
                  if (val) {
                    return (
                      val?.startsWith('-----BEGIN CERTIFICATE-----') &&
                      val?.endsWith('-----END CERTIFICATE-----')
                    );
                  }
                  return false;
                },
              ),
          }),
        ),
      type: yup
        .string()
        .required(intl.formatMessage(IdentityProvidersMessages.userTypeRequiredError)),
      prompt: yup
        .string()
        .nullable()
        .notRequired()
        .when('prompt', {
          is: (value: string | undefined | null) => !!value && value?.length,
          then: (rules) =>
            rules.oneOf(
              ['none', 'login', 'consent', 'select_account'],
              'Prompt must be one of: none, login, consent, select_account ',
            ),
        }),
    },
    [['prompt', 'prompt']],
  );
  return useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
};
